import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, RefreshCw, Sparkles, MessageCircle, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/admin/DashboardLayout";

export default function Keywords() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/keywords").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setBusy(true);
    try { await api.post("/admin/keywords/refresh"); toast.success("Ricerca avviata, risultati tra ~30 secondi"); setTimeout(() => { load(); setBusy(false); }, 30000); }
    catch (e) { toast.error(errorMessage(e)); setBusy(false); }
  };
  const generate = async (kw) => {
    try { await api.post("/admin/articles/generate", { keyword: kw.keyword, pet_type: /gatt/i.test(kw.keyword) ? "gatto" : /can[ei]\b/i.test(kw.keyword) ? "cane" : "entrambi" }); toast.success("Bozza in generazione"); navigate("/dashboard/content"); }
    catch (e) { toast.error(errorMessage(e)); }
  };

  const latest = data?.runs?.[0];
  return (
    <div data-testid="keywords-page">
      <PageHeader eyebrow="Ogni lunedì 7:30" title="Idee articoli" help="Le ricerche che la gente fa su Google su cani, gatti e polizze. Premi 'Genera articolo' su una keyword e la bozza arriva in Articoli.">
        <button onClick={refresh} disabled={busy} className="btn-primary !py-2 text-sm" data-testid="keywords-refresh-btn"><RefreshCw size={14} className={busy ? "animate-spin" : ""} /> {busy ? "Ricerca..." : "Cerca ora"}</button>
      </PageHeader>
      <div className="grid gap-3 sm:grid-cols-2 mb-6">
        {[["DataForSEO", data?.dataforseo, "Volumi reali Google Ads Italia. Senza chiavi usa la stima AI (Claude)."], ["Twilio WhatsApp", data?.twilio, "Follow-up WhatsApp a 3 e 7 giorni ai lead che non rispondono."]].map(([n, ok, d]) => (
          <div key={n} className="surface p-4 flex items-start gap-3" data-testid={`integration-status-${n.toLowerCase().replace(/\s+/g, "-")}`}>
            {ok ? <CheckCircle2 size={18} className="text-emerald-500 mt-0.5" /> : <Circle size={18} className="text-amber-500 mt-0.5" />}
            <div><p className="font-medium text-sm">{n} <span className={`font-mono text-[10px] rounded px-1.5 py-0.5 ml-1 ${ok ? "bg-emerald-500/15 text-emerald-500" : "bg-amber-500/15 text-amber-500"}`}>{ok ? "ATTIVO" : "CHIAVI MANCANTI"}</span></p><p className="text-xs text-muted-foreground mt-0.5">{d}</p></div>
          </div>
        ))}
      </div>
      {!latest && <p className="surface p-10 text-center text-muted-foreground" data-testid="keywords-empty">Nessuna ricerca ancora. Premi "Cerca ora" o attendi lunedì.</p>}
      {latest && (
        <div className="surface divide-y divide-border" data-testid="keywords-list">
          <div className="px-5 py-4 flex items-center gap-2 text-sm"><Search size={15} className="text-emerald-500" /><b>Ultima ricerca</b><span className="text-muted-foreground">· {fmtDate(latest.created_at)} · fonte {latest.source === "ai" ? "stima AI" : "DataForSEO"}</span></div>
          {latest.items.map((k, i) => (
            <div key={k.keyword + i} className="px-5 py-3.5 flex flex-wrap items-center gap-3" data-testid={`keyword-row-${i}`}>
              <div className="min-w-0 flex-1"><p className="font-medium">{k.keyword}</p><p className="text-xs text-muted-foreground">{k.why || (k.competition ? `competizione ${k.competition}` : "")}</p></div>
              {k.volume != null && <span className="num text-sm text-muted-foreground">{Number(k.volume).toLocaleString("it-IT")}/mese</span>}
              {k.intent && <span className={`rounded-full px-2 py-0.5 text-[11px] ${k.intent === "commerciale" ? "bg-emerald-500/15 text-emerald-600" : "bg-sky-500/15 text-sky-600"}`}>{k.intent}</span>}
              <button onClick={() => generate(k)} className="btn-ghost !py-1.5 !px-3 text-xs" data-testid={`keyword-generate-${i}`}><Sparkles size={13} /> Genera articolo</button>
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-6 flex items-start gap-2"><MessageCircle size={14} className="shrink-0 mt-0.5" /> Per attivare Twilio e DataForSEO inserisci le chiavi nelle variabili TWILIO_ACCOUNT_SID / TWILIO_AUTH_TOKEN / TWILIO_WHATSAPP_FROM e DATAFORSEO_LOGIN / DATAFORSEO_PASSWORD del backend.</p>
    </div>
  );
}
